import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { CaretDownIcon } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { Button } from '@/ui/button/Button';
import { getDisplayError } from '@/common-utils/get-display-error';

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const [showDetails, setShowDetails] = useState(false);
  const displayError = getDisplayError(error);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-surface-1">
      <div className="flex flex-col items-center max-w-md w-full px-6">
        {/* Error message */}
        <p className="text-sm text-fg">{displayError}</p>

        {/* Divider */}
        <div className="w-full border-t border-line my-6" />

        {/* Description + retry */}
        <p className="text-xs text-fg-secondary text-center leading-relaxed">
          An unexpected error has occurred.{' '}
          <Button variant="ghost" onClick={() => resetErrorBoundary()}>
            Try again
          </Button>
        </p>

        {/* Expandable developer details */}
        <Button
          variant="ghost"
          size={7}
          onClick={() => setShowDetails(!showDetails)}
          iconLeft={
            <CaretDownIcon
              size={10}
              weight="bold"
              className={`transition-transform duration-150 ${showDetails ? 'rotate-0' : '-rotate-90'}`}
            />
          }
        >
          Developer details
        </Button>

        {showDetails && (
          <pre className="mt-3 w-full rounded-lg border border-line bg-surface-2 px-4 py-3 text-2xs leading-relaxed font-mono text-fg-secondary whitespace-pre-wrap wrap-break-word max-h-48 overflow-y-auto">
            {error instanceof Error ? error.stack : displayError}
          </pre>
        )}
      </div>
    </div>
  );
};

export const DefaultErrorBoundary: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      }}
      onReset={() => {
        window.location.reload();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
